import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #transitionSequence = 0;
  #opener = null;
  #scrollY = 0;
  #bodyStyles = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#armDeferredHydration();
    this.#setupAnimatedElementListeners();
  }

  /**
   * PERF: deferred drawer hydration.
   *
   * header-drawer.liquid parks the ~800-element drawer body inside an inert
   * <template data-drawer-content>. Keeping it out of the document tree means
   * first paint pays zero parse / style / layout / paint cost for markup the
   * shopper has not asked for yet. We swap it in on the first signal that the
   * drawer is about to be needed, which always happens before the CSS open
   * animation can run, so the swap is invisible.
   *
   * Hydration is also armed on `requestIdleCallback` so that assistive
   * technology, deep links and keyboard users find a complete DOM even if they
   * never produce a pointer event.
   */
  #armDeferredHydration() {
    const template = this.querySelector('template[data-drawer-content]');
    if (!template) return;

    const hydrate = () => this.#hydrateDrawer();

    // Any of these means the drawer is imminent — pointerdown/touchstart fire
    // before click, so the markup is in place before `open()` runs. `focusin`
    // covers keyboard and screen-reader users who tab to the hamburger, so
    // assistive technology always finds a complete menu.
    for (const type of ['pointerdown', 'touchstart', 'focusin', 'keydown', 'mouseenter']) {
      this.addEventListener(type, hydrate, { once: true, passive: true, capture: true });
    }

    // NOTE: deliberately NO requestIdleCallback / timeout fallback here.
    // An idle fallback re-inserted the ~800 drawer elements a second or two
    // after load, which put the entire Style & Layout cost straight back and
    // showed up in measurement as DOM dropping by only 5 nodes instead of ~800.
    // Interaction is the correct and sufficient trigger: `open()` also calls
    // #hydrateDrawer() defensively, so no code path can find an empty drawer.
  }

  /**
   * Move the parked drawer markup into the live DOM exactly once.
   */
  #hydrateDrawer() {
    const template = this.querySelector('template[data-drawer-content]');
    if (!template || !(template instanceof HTMLTemplateElement)) return;

    const parent = template.parentElement;
    if (!parent) return;

    parent.replaceChild(template.content.cloneNode(true), template);

    // The animated elements only exist now, so their listeners must be
    // (re)attached after the clone lands.
    this.#setupAnimatedElementListeners();
    this.dispatchEvent(new CustomEvent('drawer:hydrated', { bubbles: true }));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.documentElement.removeAttribute('scroll-lock');
    this.#unlockPage(false);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const details = this.#getDetailsElement(event);
    // After a nested panel closes, focus returns to its summary inside the now-closed
    // details element. A second Escape should close the root drawer, not re-close the
    // inactive submenu and leave the page scroll-locked.
    this.#close(details !== this.refs.details && !details.open ? this.refs.details : details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    // PERF safety net: if a code path reaches open() before any hydration
    // signal fired (e.g. a programmatic open), swap the markup in first so the
    // drawer is never empty.
    this.#hydrateDrawer();

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#transitionSequence += 1;
    if (details === this.refs.details) {
      this.#opener = summary;
      this.#lockPage();
      document.documentElement.classList.add('orlaven-drawer-open');
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          trapFocus(drawer || details);
          const savedScrollTop = Number(details.dataset.drawerScrollTop || 0);
          const scrollContainer = details.querySelector('.menu-drawer__inner-submenu');
          if (scrollContainer instanceof HTMLElement && savedScrollTop > 0) {
            scrollContainer.scrollTop = savedScrollTop;
          }
          this.#restoreScrollPosition();
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    return this.#close(this.refs.details);
  }

  /** Close the drawer before handing control to Horizon's existing predictive search. */
  async openSearch() {
    await this.#close(this.refs.details);
    const search = document.querySelector('#search-modal');
    if (search && 'showDialog' in search && typeof search.showDialog === 'function') {
      search.showDialog();
    } else {
      window.location.assign('/search');
    }
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return Promise.resolve();

    const sequence = ++this.#transitionSequence;
    const scrollContainer = details.querySelector('.menu-drawer__inner-submenu');
    if (scrollContainer instanceof HTMLElement) {
      details.dataset.drawerScrollTop = String(scrollContainer.scrollTop);
    }

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    const parentOpenDetails = details.parentElement?.closest('details.menu-open');
    const isNestedSubmenu = parentOpenDetails instanceof HTMLDetailsElement && parentOpenDetails !== this.refs.details;
    if (!isNestedSubmenu) {
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    return onAnimationEnd(
      drawer || details,
      () => {
        if (sequence !== this.#transitionSequence) return;
        reset(details);
        if (details === this.refs.details) {
          document.documentElement.classList.remove('orlaven-drawer-open');
          document.documentElement.removeAttribute('scroll-lock');
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          this.#unlockPage();
          this.#opener?.focus({ preventScroll: true });
        } else {
          const parentSubmenu = details.parentElement?.closest('.menu-drawer__submenu');
          trapFocus(parentSubmenu instanceof HTMLElement ? parentSubmenu : this.refs.menuDrawer);
          summary.focus({ preventScroll: true });
        }
      },
      { subtree: false }
    );
  }

  #lockPage() {
    if (this.#bodyStyles) return;
    this.#scrollY = window.scrollY;
    // The native scroll-lock attribute keeps the viewport stable without moving
    // the body, which avoids iOS Safari jumps and preserves playing media.
    this.#bodyStyles = {};
  }

  #unlockPage(restoreScroll = true) {
    if (!this.#bodyStyles) return;
    this.#bodyStyles = null;
    if (restoreScroll) this.#restoreScrollPosition();
  }

  #restoreScrollPosition() {
    if (Math.abs(window.scrollY - this.#scrollY) <= 1) return;
    const previousScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, this.#scrollY);
    requestAnimationFrame(() => {
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
